import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent, type RefObject } from 'react';
import { Icon } from '@/ui/Icon';
import { InputField } from '@/ui/Field';
import { Popover, usePopover } from '@/ui/Popover';
import { isMac } from '@/lib/keys';
import { cleanPastedHtml } from './html';
import { escapeHtml, normaliseUrl } from './logic';

// The message box: a contenteditable area that keeps HTML, with bold, italic, links and lists.

export const MOD = isMac ? '⌘' : 'Ctrl';

export interface EditorHandle {
  el: () => HTMLDivElement | null;
  /** Puts the caret at the start, the end, or on the empty line under the greeting. */
  focus: (where?: 'start' | 'end' | 'afterGreeting') => void;
  /** Types text where the caret last was in the message. */
  insertText: (text: string) => void;
  setHtml: (html: string) => void;
  html: () => string;
  /** The last place the caret was inside the message, kept while a menu has focus. */
  range: () => Range | null;
  /** Gives focus back with the selection where it was. */
  restore: () => void;
  /** Reports the current content after a change made with a command. */
  sync: () => void;
}

interface EditorProps {
  initialHtml: string;
  onChange: (html: string) => void;
  /** Cmd/Ctrl K inside the message: add a link. */
  onLinkKey?: () => void;
  /** Files pasted from the clipboard. */
  onPasteFiles?: (files: File[]) => void;
  invalid?: boolean;
  describedBy?: string;
}

function inside(root: HTMLElement, node: Node | null): boolean {
  return !!node && (node === root || root.contains(node));
}

export const Editor = forwardRef<EditorHandle, EditorProps>(function Editor({ initialHtml, onChange, onLinkKey, onPasteFiles, invalid, describedBy }, ref) {
  const el = useRef<HTMLDivElement>(null);
  const saved = useRef<Range | null>(null);
  const report = useRef(onChange);
  report.current = onChange;
  const first = useRef(initialHtml);

  // The editor owns its content after the first paint; React never rewrites it.
  useLayoutEffect(() => {
    if (el.current) el.current.innerHTML = first.current;
  }, []);

  useEffect(() => {
    const track = () => {
      const root = el.current;
      const sel = document.getSelection();
      if (!root || !sel || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      if (inside(root, r.startContainer)) saved.current = r.cloneRange();
    };
    document.addEventListener('selectionchange', track);
    return () => document.removeEventListener('selectionchange', track);
  }, []);

  const placeCaret = (where: 'start' | 'end' | 'afterGreeting') => {
    const root = el.current;
    if (!root) return;
    const r = document.createRange();
    const lines = root.children;
    if (where === 'afterGreeting' && lines.length > 1 && /^(hi|hello|dear)\b/i.test(lines[0]?.textContent ?? '')) {
      r.setStart(lines[1]!, 0);
    } else {
      r.selectNodeContents(root);
      r.collapse(where === 'start');
    }
    r.collapse(true);
    const sel = document.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
    saved.current = r.cloneRange();
  };

  const restore = () => {
    const root = el.current;
    if (!root) return;
    root.focus({ preventScroll: true });
    const sel = document.getSelection();
    const r = saved.current;
    if (r && inside(root, r.startContainer)) {
      sel?.removeAllRanges();
      sel?.addRange(r);
    } else placeCaret('end');
  };

  useImperativeHandle(ref, () => ({
    el: () => el.current,
    focus(where = 'end') {
      el.current?.focus({ preventScroll: true });
      placeCaret(where);
    },
    insertText(text) {
      restore();
      document.execCommand('insertText', false, text);
      report.current(el.current?.innerHTML ?? '');
    },
    setHtml(html) {
      if (!el.current) return;
      el.current.innerHTML = html;
      saved.current = null;
      report.current(html);
    },
    html: () => el.current?.innerHTML ?? '',
    range: () => saved.current,
    restore,
    sync: () => report.current(el.current?.innerHTML ?? ''),
  }));

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length && onPasteFiles) {
      e.preventDefault();
      onPasteFiles(files);
      return;
    }
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    e.preventDefault();
    if (html) document.execCommand('insertHTML', false, cleanPastedHtml(html));
    else if (text) document.execCommand('insertText', false, text);
    report.current(el.current?.innerHTML ?? '');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const mod = isMac ? e.metaKey : e.ctrlKey;
    if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k' && onLinkKey) {
      e.preventDefault();
      onLinkKey();
    }
  };

  return (
    <div
      ref={el}
      className={['editor', invalid ? 'invalid' : ''].filter(Boolean).join(' ')}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label="Message"
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      spellCheck
      onInput={() => report.current(el.current?.innerHTML ?? '')}
      onPaste={onPaste}
      onKeyDown={onKeyDown}
    />
  );
});

/* ---------- formatting bar ---------- */

interface Marks {
  bold: boolean;
  italic: boolean;
  ul: boolean;
  ol: boolean;
  link: boolean;
}

const NONE: Marks = { bold: false, italic: false, ul: false, ol: false, link: false };

function linkAt(root: HTMLElement | null, r: Range | null): HTMLAnchorElement | null {
  if (!root || !r) return null;
  const node = r.startContainer;
  const elm = node instanceof Element ? node : node.parentElement;
  const a = elm?.closest('a');
  return a && root.contains(a) ? a : null;
}

function state(cmd: string): boolean {
  try {
    return document.queryCommandState(cmd);
  } catch {
    return false;
  }
}

export interface FormatBarHandle {
  openLink: () => void;
}

export const FormatBar = forwardRef<FormatBarHandle, { editor: RefObject<EditorHandle | null> }>(function FormatBar({ editor }, ref) {
  const [marks, setMarks] = useState<Marks>(NONE);
  const linkBtn = useRef<HTMLButtonElement>(null);
  const pop = usePopover('dialog');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<HTMLAnchorElement | null>(null);

  useEffect(() => {
    const read = () => {
      const root = editor.current?.el();
      const sel = document.getSelection();
      if (!root || !sel || sel.rangeCount === 0 || !inside(root, sel.anchorNode)) return;
      setMarks({
        bold: state('bold'),
        italic: state('italic'),
        ul: state('insertUnorderedList'),
        ol: state('insertOrderedList'),
        link: !!linkAt(root, sel.getRangeAt(0)),
      });
    };
    document.addEventListener('selectionchange', read);
    return () => document.removeEventListener('selectionchange', read);
  }, [editor]);

  const run = (cmd: string) => {
    const ed = editor.current;
    if (!ed) return;
    ed.restore();
    document.execCommand(cmd);
    ed.sync();
    setMarks((m) => ({ ...m, bold: state('bold'), italic: state('italic'), ul: state('insertUnorderedList'), ol: state('insertOrderedList') }));
  };

  const openLink = () => {
    const ed = editor.current;
    const a = linkAt(ed?.el() ?? null, ed?.range() ?? null);
    setEditing(a);
    setUrl(a?.getAttribute('href')?.replace(/^mailto:/, '') ?? '');
    setError(null);
    if (linkBtn.current) pop.openAt(linkBtn.current);
  };

  useImperativeHandle(ref, () => ({ openLink }));

  const apply = (e: FormEvent) => {
    e.preventDefault();
    const href = normaliseUrl(url);
    if (!href) {
      setError('Enter a web address or an email address');
      return;
    }
    const ed = editor.current;
    if (!ed) return;
    pop.close();
    ed.restore();
    const r = ed.range();
    if (editing && editing.isConnected) editing.setAttribute('href', href);
    else if (r && !r.collapsed) document.execCommand('createLink', false, href);
    else document.execCommand('insertHTML', false, `<a href="${escapeHtml(href)}">${escapeHtml(url.trim())}</a>&nbsp;`);
    ed.sync();
  };

  const unlink = () => {
    const ed = editor.current;
    if (!ed || !editing) return;
    pop.close();
    const parent = editing.parentNode;
    if (parent) {
      while (editing.firstChild) parent.insertBefore(editing.firstChild, editing);
      parent.removeChild(editing);
    }
    ed.restore();
    ed.sync();
  };

  // Buttons keep the caret in the message: they never take focus on mouse down.
  const keep = (e: { preventDefault: () => void }) => e.preventDefault();

  return (
    <div className="cmp-fmt" role="toolbar" aria-label="Formatting">
      <button type="button" className="icon-btn xs fmt-b" aria-label="Bold" aria-pressed={marks.bold} data-tip="Bold" data-key={`${MOD} B`} onMouseDown={keep} onClick={() => run('bold')}>
        B
      </button>
      <button type="button" className="icon-btn xs fmt-i" aria-label="Italic" aria-pressed={marks.italic} data-tip="Italic" data-key={`${MOD} I`} onMouseDown={keep} onClick={() => run('italic')}>
        I
      </button>
      <button
        ref={linkBtn}
        type="button"
        className="icon-btn xs"
        aria-label="Link"
        aria-pressed={marks.link}
        aria-haspopup="dialog"
        aria-expanded={pop.open}
        data-tip="Link"
        data-key={`${MOD} K`}
        onMouseDown={keep}
        onClick={() => (pop.open ? pop.close() : openLink())}
      >
        <Icon name="link" />
      </button>
      <button type="button" className="icon-btn xs" aria-label="Bulleted list" aria-pressed={marks.ul} data-tip="Bulleted list" onMouseDown={keep} onClick={() => run('insertUnorderedList')}>
        <Icon name="list" />
      </button>
      <button type="button" className="icon-btn xs fmt-ol" aria-label="Numbered list" aria-pressed={marks.ol} data-tip="Numbered list" onMouseDown={keep} onClick={() => run('insertOrderedList')}>
        1.
      </button>
      <Popover state={pop} role="dialog" label={editing ? 'Edit link' : 'Add a link'} width={300} maxWidth={320} align="left" className="cmp-linkpop">
        <form onSubmit={apply} noValidate>
          <InputField
            label="Link address"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError(null);
            }}
            placeholder="fieldnote.studio/work"
            error={error}
            autoComplete="off"
            spellCheck={false}
          />
          <div className="cmp-linkpop-f">
            {editing ? (
              <button type="button" className="btn btn-sm btn-ghost" onClick={unlink}>
                Remove link
              </button>
            ) : null}
            <span className="spacer" />
            <button type="submit" className="btn btn-sm btn-primary">
              {editing ? 'Save link' : 'Add link'}
            </button>
          </div>
        </form>
      </Popover>
    </div>
  );
});
